// src/components/compliance/KycHandoffQr.tsx
/**
 * «Möchten Sie die Identifikation auf Ihrem Handy machen?» (Issue #1071).
 *
 * Owner decision 24.09.2026: at the desktop this is the very first screen of
 * the self identification — question and QR code at once, without a further
 * click. The phone camera opens the capture page from the code, no second
 * login; whoever prefers the webcam stays here with one click.
 *
 * The code is short-lived (15 minutes, single use). Once it has run out we say
 * so and offer a fresh one instead of showing a picture that leads nowhere.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Monitor, RefreshCw, Smartphone } from 'lucide-react'
import complianceApi from '@/services/compliance'
import { KYC_HANDOFF_VALIDITY_MINUTES } from '@/constants/kyc'
import { qrMatrix } from '@/utils/qrCode'
import type { KycHandoffResponse } from '@/types/kyc'

interface Props {
  /** The identification the code is bound to. */
  verificationId: number
  /** Webcam or upload instead — the assistant takes over on this device. */
  onContinueOnComputer: () => void
  /** The phone has opened the code but has not submitted yet. */
  phoneStarted?: boolean
}

/** Quiet zone around the code, in modules — four is what the standard asks for. */
const QUIET_ZONE = 4

/** Remaining validity as `m:ss`. */
function formatRemaining(ms: number): string {
  const total = Math.ceil(ms / 1000)
  const minutes = Math.floor(total / 60)
  const seconds = total % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

export default function KycHandoffQr({ verificationId, onContinueOnComputer, phoneStarted }: Props) {
  const { t } = useTranslation()

  const [handoff, setHandoff] = useState<KycHandoffResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const [now, setNow] = useState<number>(() => Date.now())
  const requestedRef = useRef(false)

  const load = useCallback(async () => {
    setLoading(true)
    setFailed(false)
    try {
      const created = await complianceApi.createKycHandoff(verificationId)
      setHandoff(created)
      setNow(Date.now())
    } catch {
      setHandoff(null)
      setFailed(true)
    } finally {
      setLoading(false)
    }
  }, [verificationId])

  // The question and the code appear together — so the code is fetched as soon
  // as this screen exists. The ref keeps React 18's double-invoked effect from
  // burning a second code.
  useEffect(() => {
    if (requestedRef.current) return
    requestedRef.current = true
    void load()
  }, [load])

  // Ticking clock for the remaining validity
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [])

  const expiresAt = handoff ? Date.parse(handoff.expires_at) : Number.NaN
  const remaining = Number.isNaN(expiresAt) ? null : Math.max(0, expiresAt - now)
  const expired = remaining !== null && remaining === 0
  const matrix = useMemo(
    () => (handoff && !expired ? qrMatrix(handoff.handoff_url) : null),
    [handoff, expired],
  )

  return (
    <div data-testid="kyc-handoff" className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-gray-900">
          {t('kyc.handoff.title', 'Möchten Sie die Identifikation auf Ihrem Handy machen?')}
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          {t('kyc.handoff.lead', {
            defaultValue:
              'Scannen Sie den Code mit der Handy-Kamera. Der Code ist {{minutes}} Minuten gültig.',
            minutes: KYC_HANDOFF_VALIDITY_MINUTES,
          })}
        </p>
      </div>

      <div className="flex flex-col items-center gap-3">
        {loading && !handoff ? (
          <div
            data-testid="kyc-handoff-loading"
            className="flex h-48 w-48 items-center justify-center rounded-xl border border-gray-200 bg-gray-50"
          >
            <Loader2 size={24} className="animate-spin text-gray-400" />
          </div>
        ) : handoff && !expired ? (
          <>
            {matrix ? (
              <svg
                data-testid="kyc-handoff-qr"
                data-qr-value={handoff.handoff_url}
                role="img"
                aria-label={t('kyc.handoff.qrAlt', 'QR-Code für die Identifikation auf dem Handy')}
                viewBox={`0 0 ${matrix.length + QUIET_ZONE * 2} ${matrix.length + QUIET_ZONE * 2}`}
                shapeRendering="crispEdges"
                className="h-44 w-44 sm:h-52 sm:w-52 rounded-xl border border-gray-200 bg-white p-2"
              >
                <rect
                  width={matrix.length + QUIET_ZONE * 2}
                  height={matrix.length + QUIET_ZONE * 2}
                  fill="#ffffff"
                />
                {matrix.map((line, row) =>
                  line.map((dark, col) =>
                    dark ? (
                      <rect
                        key={`${row}-${col}`}
                        x={col + QUIET_ZONE}
                        y={row + QUIET_ZONE}
                        width={1}
                        height={1}
                        fill="#111827"
                      />
                    ) : null,
                  ),
                )}
              </svg>
            ) : (
              // An address too long for a code — then at least the link, so
              // the phone can be reached by typing or by mail.
              <a
                data-testid="kyc-handoff-link"
                href={handoff.handoff_url}
                className="break-all rounded-lg border border-gray-200 bg-gray-50 p-3 text-center text-xs text-primary-700 underline underline-offset-2"
              >
                {handoff.handoff_url}
              </a>
            )}
            {remaining !== null && (
              <p data-testid="kyc-handoff-remaining" className="text-xs text-gray-500">
                {t('kyc.handoff.remaining', {
                  defaultValue: 'Code noch {{time}} gültig',
                  time: formatRemaining(remaining),
                })}
              </p>
            )}
          </>
        ) : (
          <p
            data-testid="kyc-handoff-error"
            role="status"
            className="w-full rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
          >
            {failed
              ? t(
                  'kyc.handoff.failed',
                  'Der Code konnte nicht erzeugt werden. Erzeugen Sie einen neuen oder machen Sie hier am Computer weiter.',
                )
              : t(
                  'kyc.handoff.expired',
                  'Dieser Code ist abgelaufen. Erzeugen Sie einen neuen Code.',
                )}
          </p>
        )}

        {phoneStarted && (
          <p
            data-testid="kyc-handoff-phone-started"
            role="status"
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-primary-200 bg-primary-50 p-3 text-sm text-primary-800"
          >
            <Smartphone size={16} className="flex-shrink-0" />
            {t(
              'kyc.handoff.phoneStarted',
              'Auf dem Handy geöffnet — bitte dort abschliessen. Dieses Fenster aktualisiert sich von selbst.',
            )}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col items-stretch gap-1 sm:items-start">
          <button
            data-testid="kyc-handoff-regenerate-btn"
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <RefreshCw size={16} />
            )}
            {t('kyc.handoff.regenerate', 'Neuen Code erzeugen')}
          </button>
          {/* Only while a usable code is on screen — after expiry or an error
              there is nothing left that a new code could invalidate. */}
          {handoff && !expired && (
            <p data-testid="kyc-handoff-regenerate-hint" className="text-xs text-gray-500">
              {t('kyc.handoff.regenerateHint', 'Ein neuer Code macht den bisherigen ungültig.')}
            </p>
          )}
        </div>
        <button
          data-testid="kyc-handoff-continue-here-btn"
          type="button"
          onClick={onContinueOnComputer}
          className="flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-primary-600 hover:text-primary-800 hover:bg-primary-50"
        >
          <Monitor size={16} />
          {t('kyc.handoff.continueHere', 'Hier am Computer weitermachen')}
        </button>
      </div>
    </div>
  )
}
